import { isRouteErrorResponse } from 'react-router-dom';
import * as api_gen from './General.ts';
import { ErrorResponseImpl } from '@remix-run/router/dist/utils';


type Optional<T> = T | null;

export interface User {
    id?: number;
    email: string;
    full_name: string;
    role: "ADMIN" | "USER" | "GUEST";
    activated: boolean;
    password: string | null;
}

export interface UserUpdateRequest {
    id: number;
    email: string;
    full_name: string;
    role: "ADMIN" | "USER" | "GUEST";
    activated: boolean;
}

export interface UsersResponse {
    items: User[];
}

export interface UpdateVocabRecord {
    vocab_name: string;
    id: string;
    data_record: string[];
}

export interface NewVocabRecord {
    vocab_name: string;
    data_record: string[];
}

export interface Detail {
    year: number;
    count: number;
}

export interface Pest {
    pest_id: number;
    scientific_name: string;
    details: Detail[];
}

export interface PestCountPerMoaResponse {
    results: Pest[];
    all_frequencies: string[];
    all_pest_ids: number[];
    selected_frequencies: string[];
    selected_pest_ids: number[] | null;
}


export class ChartFilter {
    id: Optional<number>;
    countries: Optional<string[]>;
    moa_code: string;
    pest_sci_name: Optional<string[]>;
    freq_resistance: Optional<string[]>;
    years: Optional<number[]>;
    pest_ids: Optional<number[]>;

    constructor({
        id = null,
        countries = null,
        moa_code = "",
        pest_sci_name = null,
        freq_resistance = null,
        years = null,
        pest_ids = null
    }: {
        id?: Optional<number>,
        countries?: Optional<string[]>,
        moa_code?: string,
        pest_sci_name?: Optional<string[]>,
        freq_resistance?: Optional<string[]>,
        years?: Optional<number[]>,
        pest_ids?: Optional<number[]>
    } = {}) {
        this.id = id;
        this.countries = countries;
        this.moa_code = moa_code;
        this.pest_sci_name = pest_sci_name;
        this.freq_resistance = freq_resistance;
        this.years = years;
        this.pest_ids = pest_ids;
    }
}


export interface CropOrPest {
    id: number;
    common_name: string;
    scientific_name: string;
    eppo_code: string;
}

export interface Company {
    id: number;
    name_code: string;
}

export interface MoaCode {
    id: number;
    name: string;
}

export interface Country {
    id: number;
    name: string;
    code: string;
}

export interface MonitoringIntensity {
    id: number;
    intensity: string;
}


export interface MonitoringType {
    id: number;
    type: string;
}

export interface ResistanceFrequency {
    id: number;
    frequency: string;
}


export interface ResistanceFactor {
    id: number;
    factor: string;
}


export interface ResistanceMechanism {
    id: number;
    mechanism: string;
}


export interface ResistanceRelevance {
    id: number;
    relevance: string;
}

export interface GeoDistribution {
    id: number;
    distribution: string;
}


export interface DensityBin {
    id?: number;
    year: number;
    min_val: number;
    max_val: number;
    color: string;
    label: string;
}


export interface ResistanceData {
    id?: number;
    countries: string[] | string;
    year: string;
    frac_moa_code: string;
    pest_sci_name: string;
    crop_sci_name: string;
    companies: string[];
    monitoring_type: string;
    monitoring_intensity: string;
    resistance_frequency: string[];
    resistance_mechanism: string;
    mechanism_detail: string;
    geo_distribution: string;
    resistance_factor: string;
    resistance_field_relevance: string;
    comments: string;
};


export class FracApi {

    constructor() {
    }


    async getResData() {
        var allData = [];
        var url = "resistance_monitoring_data";


        var post_data = {
            "draw": 1,
            "order": [
                {
                    "column": 0,
                    "dir": "asc"
                }
            ],
            "start": 0,
            "length": 500,
            "search": {
                "value": "",
                "regex": false
            }
        }
        var all_data = [];
        var has_more = true;

        while (has_more) {
            var data = await api_gen.postSimple(url, post_data);
            if (data.data.length < 500) {
                has_more = false;
            }

            all_data = all_data.concat(data.data);
            post_data.start += 500;
        }

        return all_data;
    }

    async createMonitoringRecord(data: ResistanceData) {
        var url = "CreateMonitoringRecordNew";
        var response = await api_gen.postSimple(url, data);

        return response;
    }


    async updateMonitoringRecord(data: ResistanceData) {
        var url = "UpdateMonitoringRecordNew";
        var response = await api_gen.postSimple(url, data);
        return response;
    }

    async getPestCountPerMoa(inputFilter: ChartFilter): Promise<PestCountPerMoaResponse> {
        try {
            const response = await api_gen.postSimple('pest_count_per_moa', inputFilter);
            return response;

        } catch (error) {
            console.error('Error calling the API:', error);
            throw error;
        }
    }

    async getLookupData(name) {
        var allData = [];
        var url = "lookup_data/" + name;
        var data = await api_gen.getSimpleData(url);
        return data;
    }

    async getCompanyLookupData(): Promise<Company[]> {
        const data = await this.getLookupData("companies");
        return data;
    }

    async getCountryLookupData(): Promise<Country[]> {
        const data = await this.getLookupData("countries");

        // sort countries by name
        data.sort((a, b) => a.name.localeCompare(b.name));

        return data;
    }

    async getMoaCodesLookupData(): Promise<MoaCode[]> {
        const data = await this.getLookupData("moa");
        return data;
    }



    async getMonitoringIntensityLookupData(): Promise<MonitoringIntensity[]> {
        const data = await this.getLookupData("monitoring_intensity");
        return data;
    }

    async getMonitoringTypeLookupData(): Promise<MonitoringType[]> {
        const data = await this.getLookupData("monitoring_type");
        return data;
    }

    async getResistanceFrequencyLookupData(): Promise<ResistanceFrequency[]> {
        const data = await this.getLookupData("resistance_frequency");
        return data;
    }

    async getResistanceFactorLookupData(): Promise<ResistanceFactor[]> {
        const data = await this.getLookupData("resistance_factor");
        return data;
    }


    async getResistanceMechanismLookupData(): Promise<ResistanceMechanism[]> {
        const data = await this.getLookupData("resistance_mechanism");
        return data;
    }

    async getResistanceRelevanceLookupData(): Promise<ResistanceRelevance[]> {
        const data = await this.getLookupData("resistance_relevance");
        return data;
    }

    async getGeoDistributionLookupData(): Promise<GeoDistribution[]> {
        const data = await this.getLookupData("geo_distribution");
        return data;
    }

    async updateVocabularyRecord(data: UpdateVocabRecord) {
        var url = "UpdateVocabularyRecordNew";
        var response = await api_gen.postSimple(url, data);
        return response;
    }

    async deleteVocabularyRecord(name: string, rec_id: string) {
        var url = "DeleteVocabularyRecord";
        const data = { vocab_name: name, id: rec_id };
        var response = await api_gen.postSimple(url, data);
        return response;
    }

    async createVocabularyRecord(data: NewVocabRecord) {
        var url = "CreateVocabularyRecord";
        var response = await api_gen.postSimple(url, data);
        return response;
    }

    async getUsers(): Promise<User[]> {
        const response = await api_gen.getSimpleData('users');

        const usersResp = response as UsersResponse;
        return usersResp.items;
    }

    async updateUser(data: UserUpdateRequest) {
        var url = "users";
        var response = await api_gen.putSimple(url, data);
        return response;
    }



    async changePassword(old_password: string, new_password: string): Promise<null | string> {
        var url = "changePasswordNew";

        var data = {
            old_password: old_password,
            new_password: new_password
        };

        try {
            await api_gen.putSimple(url, data);
            return null;

        } catch (error) {
            return error.message;
        }
    }


    async deleteUser(email: string) {
        var url = "DeleteUser";
        var user = { email: email };
        var response = await api_gen.postSimple(url, user);
        return response;
    }

    async createUser(data: User) {
        var url = "CreateUserNew";
        var response = await api_gen.postSimple(url, data);
        return response;
    }

    urlEncode(str: string) {
        return encodeURIComponent(str);
    }

    async searchCrops(search_text: string): Promise<CropOrPest[]> {
        const response = await api_gen.getSimpleData('searchCrops?text=' + this.urlEncode(search_text));
        return response;
    }

    async searchPests(search_text: string): Promise<CropOrPest[]> {
        const response = await api_gen.getSimpleData('searchPests?text=' + this.urlEncode(search_text));
        return response;
    }


    async getCropWithSciName(sci_name: string): Promise<CropOrPest> {
        const response = await api_gen.getSimpleData('getCropWithSciName?sci_name=' + this.urlEncode(sci_name));
        return response as CropOrPest;
    }

    async getPestWithSciName(sci_name: string): Promise<CropOrPest> {
        const response = await api_gen.getSimpleData('getPestWithSciName?sci_name=' + this.urlEncode(sci_name));
        return response as CropOrPest;
    }

    async deleteMonitoringRecord(rec_id: number) {
        var url = "DeleteMonitoringRecord";
        const data = { id: rec_id };
        var response = await api_gen.postSimple(url, data);
        return response;
    }

    async uploadFile(file: File): Promise<Blob | null | string> {
        var url = "importExcelFile";
        var response = await api_gen.uploadFile(url, file);
        return response;
    }

    getTemplateUrl() {
        return api_gen.getBaseApiUrl() + "getTemplate";
    }

    async getTemplate(): Promise<Blob | null> {
        var url = "getTemplate";
        var response = await api_gen.getFile(url);
        return response;
    }

    async previewMapData(files: File[], year: number): Promise<any[]> {
        return await api_gen.uploadMultipleFiles(`preview_map_data?year=${year}`, files);
    }

    async commitMapData(records: any[]): Promise<any> {
        return await api_gen.postSimple("commit_map_data", records);
    }

    async getGlobalMapDensity(filter: ChartFilter): Promise<any> {
        return await api_gen.postSimple("global_map_density", filter);
    }

    async downloadMapData(year: number): Promise<Blob | null> {
        return await api_gen.getFile(`download_map_data?year=${year}`);
    }

    async clearMapData(year: number): Promise<any> {
        return await api_gen.deleteSimple(`clear_map_data?year=${year}`);
    }

    async getMapDensityBins(year: number): Promise<DensityBin[]> {
        return await api_gen.getSimpleData(`map_density_bins?year=${year}`);
    }

    async saveMapDensityBins(year: number, bins: DensityBin[]): Promise<any> {
        return await api_gen.postSimple(`map_density_bins?year=${year}`, bins);
    }

    async getMapMoaCodesByYear(year: number): Promise<string[]> {
        return await api_gen.getSimpleData(`map_moa_codes_by_year?year=${year}`);
    }
}
